/**
 * ============================================
 * APEX MEDICAL GROUP - SITE CONFIGURATION
 * ============================================
 * 
 * Edit the text below to update your website.
 * After making changes:
 * 1. Save the file
 * 2. Redeploy on Emergent
 * 
 * ============================================
 */

const siteConfig = {
  // ==========================================
  // COMPANY INFORMATION
  // ==========================================
  company: {
    name: "Apex Medical Group",
    tagline: "Physician-Led Healthcare Excellence",
    description: "Apex Medical Group is a physician-led healthcare provider offering comprehensive medical services for individuals and professional medical coverage for events of all sizes.",
    yearFounded: "2024",
  },

  // ==========================================
  // CONTACT INFORMATION
  // ==========================================
  contact: {
    phone: "+1-(758)-488-8433 | +1-(758)-485-0863 | +1-(758)-724-5394",
    phones: [
      "+1-(758)-488-8433",
      "+1-(758)-485-0863",
      "+1-(758)-724-5394",
    ],
    email: "info@apexmedical.lc",
    eventsEmail: "events@apexmedical.lc",
    address: "Corinth, Gros-Islet",
    city: "Gros-Islet",
    country: "Saint Lucia",
    fullAddress: "Corinth, Gros-Islet, Saint Lucia",
  },

  // ==========================================
  // SOCIAL MEDIA & MESSAGING
  // ==========================================
  social: {
    // WhatsApp - format: country code + number without + or spaces
    whatsapp: {
      enabled: true,
      number: "17584888433", // +1 (758) 488-8433 without formatting
      message: "Hello! I'd like to inquire about Apex Medical Group services.",
    },
    // Facebook
    facebook: {
      enabled: true,
      url: "https://facebook.com/apexmedicallc", // Update when profile is created
    },
    // Instagram
    instagram: {
      enabled: true,
      url: "https://instagram.com/apexmedicallc", // Update when profile is created
    },
  },

  // ==========================================
  // LOGO
  // ==========================================
  logo: {
    url: "https://customer-assets.emergentagent.com/job_emr-payment-track/artifacts/s9yj95ue_Photoroom_20260128_234628.PNG",
    alt: "Apex Medical Group Logo",
  },

  // ==========================================
  // HOMEPAGE TEXT
  // ==========================================
  homepage: {
    headline: "Your Health,",
    headlineHighlight: "Our Priority",
    subtitle: "Apex Medical Group is a physician-led healthcare provider offering comprehensive medical services for individuals and professional medical coverage for events of all sizes.",
    
    // Patient Services Card
    patientCard: {
      title: "Patient Services",
      subtitle: "Request an appointment with our healthcare team",
      buttonText: "Request Appointment",
      features: [
        "Fully-licensed physicians",
        "Same-week appointments available",
        "Convenient scheduling options",
      ],
    },
    
    // Event Services Card
    eventCard: {
      title: "Event Medical Services",
      subtitle: "Physician-led medical coverage for your events",
      buttonText: "Request a Quote",
      features: [
        "Events from 500 to 20,000+ attendees",
        "Licensed nurses, EMTs, paramedics & MDs",
        "Single or multi-day coverage",
      ],
    },

    // Services Section
    services: [
      { title: "Primary Care", description: "Comprehensive healthcare for all ages" },
      { title: "Urgent Care", description: "Same-day appointments available" },
      { title: "Preventive Care", description: "Annual physicals & screenings" },
      { title: "Event Coverage", description: "Physician-led teams for any event size" },
    ],

    // Footer
    footerText: "Physician-led healthcare services in Saint Lucia",
  },

  // ==========================================
  // PATIENT REQUEST PAGE TEXT
  // ==========================================
  patientRequest: {
    title: "Request an Appointment",
    subtitle: "Fill out the form below and our team will contact you to schedule your visit",
    
    emergencyNotice: "Medical Emergency? Call 911 immediately or go to your nearest emergency room. This form is for non-emergency appointment requests only.",
    
    features: [
      { title: "Expert Care", description: "Fully-licensed physicians" },
      { title: "Quick Response", description: "24-48 hour callback" },
      { title: "Secure & Private", description: "Your data is protected" },
    ],

    formTitle: "Appointment Request Form",
    formSubtitle: "Please provide your information and describe your medical concern. Fields marked with * are required.",
    
    submitButton: "Submit Appointment Request",
    
    successTitle: "Request Submitted!",
    successMessage: "Thank you for choosing Apex Medical Group. Our team will review your request and contact you within 24-48 hours.",
    
    consentText: "By submitting this form, you consent to be contacted by Apex Medical Group regarding your appointment request.",
    
    needHelp: "Need Immediate Assistance?",
  },

  // ==========================================
  // EVENT QUOTE PAGE TEXT
  // ==========================================
  eventQuote: {
    title: "Event Medical Services",
    subtitle: "Physician-led medical coverage for events of all sizes",
    
    heroTitle: "Ready to Secure Your Event?",
    heroSubtitle: "Get a customized quote for professional medical coverage",
    heroButton: "Request a Quote Now",

    features: [
      { title: "Licensed Medical Teams", description: "Nurses, EMTs, Paramedics & Physicians" },
      { title: "All Event Sizes", description: "From 500 to 20,000+ attendees" },
      { title: "Flexible Coverage", description: "Single day or multi-day events" },
    ],

    formTitle: "Request a Quote",
    formSubtitle: "Fill out the form below and our team will provide a customized quote for your event.",
    
    submitButton: "Submit Quote Request",
    
    successTitle: "Quote Request Submitted!",
    successMessage: "Thank you for your interest in Apex Medical Group's event medical services. Our team will review your request and contact you within 24-48 hours.",
    
    consentText: "By submitting this form, you agree to be contacted by Apex Medical Group regarding your event medical services inquiry.",
    
    needHelp: "Need Immediate Assistance?",
  },

  // ==========================================
  // LOGIN PAGE TEXT
  // ==========================================
  login: {
    subtitle: "Electronic Medical Records System",
    signInTitle: "Sign in",
    signInSubtitle: "Enter your credentials to access the system",
    signInButton: "Sign in",
    noAccountText: "Contact your administrator for account access",
    quotePromo: "Need event medical services?",
    quoteButton: "Request a Quote",
  },

  // ==========================================
  // EMAIL SETTINGS
  // ==========================================
  email: {
    fromName: "Apex Medical Group",
    patientConfirmationSubject: "Appointment Request Received",
    eventConfirmationSubject: "Quote Request Received",
    emergencyDisclaimer: "If this is a medical emergency, please call emergency services immediately.",
  },

  // ==========================================
  // VISIT TYPES (for patient request form)
  // ==========================================
  visitTypes: [
    "New Patient Visit",
    "Follow-up Visit",
    "Annual Physical",
    "Urgent Care",
    "Specialist Consultation",
    "Telemedicine Visit",
    "Prescription Refill",
    "Lab Work / Testing",
    "Other",
  ],

  // ==========================================
  // COMMON MEDICAL CONDITIONS (for patient request form)
  // ==========================================
  medicalConditions: [
    "Diabetes",
    "Hypertension (High Blood Pressure)",
    "Heart Disease",
    "Asthma",
    "COPD",
    "Arthritis",
    "Cancer",
    "Thyroid Disorder",
    "Depression/Anxiety",
    "Kidney Disease",
    "Liver Disease",
    "Stroke",
    "Epilepsy/Seizures",
    "HIV/AIDS",
    "Autoimmune Disorder",
    "None of the above",
  ],

  // ==========================================
  // EVENT TYPES (for event quote form)
  // ==========================================
  eventTypes: [
    "Concert",
    "Sporting Event",
    "Festival",
    "Corporate Event",
    "Private Event",
    "Marathon/Run",
    "Conference",
    "Other",
  ],

  // ==========================================
  // COVERAGE LEVELS (for event quote form)
  // ==========================================
  coverageLevels: [
    "Basic First Aid",
    "BLS (Basic Life Support)",
    "ALS (Advanced Life Support)",
    "Ambulance Standby",
    "Physician-Led Medical Team",
    "Full Field Hospital",
  ],

  // ==========================================
  // ATTENDEE OPTIONS (for event quote form)
  // ==========================================
  attendeeOptions: [
    "500",
    "1,000",
    "2,000",
    "5,000",
    "10,000",
    "20,000+",
  ],
};

export default siteConfig;
